/** keep-alive로 숨긴 라우트 안에 포커스가 남으면 휠 스크롤이 보이는 페이지(.cp-content)로 전달되지 않는다. */

import React from 'react';

function isElementInHiddenKeepAliveSubtree(el: HTMLElement): boolean {
  if (el.closest('[aria-hidden="true"]')) return true;
  let cur: HTMLElement | null = el;
  while (cur && cur !== document.documentElement) {
    if (window.getComputedStyle(cur).display === 'none') return true;
    cur = cur.parentElement;
  }
  return false;
}

/** 탐색 스크롤 포커스를 줄 수 있는 래퍼인지(보이는 페이지만) */
export function isElementVisibleForBrowseScrollFocus(el: HTMLElement | null | undefined): el is HTMLElement {
  if (!el) return false;
  if (isElementInHiddenKeepAliveSubtree(el)) return false;
  return el.clientHeight > 0 && el.getClientRects().length > 0;
}

export function blurActiveElementIfInHiddenKeepAliveRoute(): void {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || active === document.body) return;
  if (isElementInHiddenKeepAliveSubtree(active)) {
    active.blur();
  }
}

/** 라우트 전환 직후 + 탐색 스크롤 완료 후 — 숨겨진 표 스크롤 래퍼 포커스를 해제 */
export function releaseTrappedKeepAliveFocus(_pathname?: string): void {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    blurActiveElementIfInHiddenKeepAliveRoute();

    const traps = document.querySelectorAll<HTMLElement>(
      '.cp-program-progress-scroll-wrap, .cp-annual-progress-table-wrap[tabindex="-1"]'
    );
    traps.forEach((el) => {
      if (!isElementInHiddenKeepAliveSubtree(el)) return;
      if (document.activeElement === el || el.contains(document.activeElement)) {
        (document.activeElement as HTMLElement | null)?.blur?.();
      }
      if (document.activeElement === el) {
        el.blur();
      }
    });

    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || active === document.body) return;
    if (!isElementInHiddenKeepAliveSubtree(active)) return;
  }
}

/** 보이는 탐색 스크롤 래퍼에만 포커스(휠 스크롤 가로채기 방지) */
export function focusBrowseScrollWrapIfVisible(el: HTMLElement | null | undefined): void {
  if (!isElementVisibleForBrowseScrollFocus(el)) return;
  el.focus({ preventScroll: true });
}

/** keep-alive 탭이 숨겨질 때 표 스크롤 래퍼 포커스를 해제 */
export function useReleaseTrappedKeepAliveFocusWhenInactive(isActive: boolean): void {
  React.useLayoutEffect(() => {
    if (isActive) return;
    releaseTrappedKeepAliveFocus();
  }, [isActive]);
}

/**
 * 숨겨진 keep-alive 트리로 포커스가 들어가면 즉시 blur.
 * 라우트 전환 직후 비동기 focus(표 탐색 래퍼 등)가 다시 잡히면
 * .cp-content 휠 스크롤이 먹통이 되고, 새로고침 전까지 유지되는 경우가 있다.
 */
export function installKeepAliveFocusGuard(): () => void {
  const onFocusIn = (e: FocusEvent) => {
    const t = e.target;
    if (!(t instanceof HTMLElement) || t === document.body) return;
    if (!isElementInHiddenKeepAliveSubtree(t)) return;
    t.blur();
    releaseTrappedKeepAliveFocus();
  };
  document.addEventListener('focusin', onFocusIn, true);
  return () => {
    document.removeEventListener('focusin', onFocusIn, true);
  };
}

/**
 * 모달이 없는데 body overflow/position 잠금만 남은 경우 해제.
 * (중첩 모달의 prev 복원·언마운트 타이밍으로 overflow:hidden / position:fixed 가 남는 경우)
 */
export function releaseOrphanedBodyScrollLock(): void {
  if (document.querySelector('[aria-modal="true"]')) return;
  const { overflow, position, top, width } = document.body.style;
  if (overflow !== 'hidden' && position !== 'fixed' && !top && !width) return;
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
}
